/**
 * authFetch.js — Fetch authentifié avec cookies httpOnly + refresh silencieux
 *
 * v10.5 : Migration httpOnly cookies (sécurité XSS)
 *
 * - Le navigateur envoie automatiquement les cookies httpOnly (access_token + refresh_token)
 * - Si l'access token expire (401 + code TOKEN_EXPIRED), tentative de refresh silencieux
 * - Si le refresh échoue, déconnexion et redirection login
 * - Compatibilité mobile : getAuthHeaders() retourne le Bearer token si présent en localStorage
 */

let isRefreshing = false;
let refreshQueue = []; // Requêtes en attente pendant le refresh

// SEC-11 : on ne touche à localStorage pour le token QUE dans le WebView
// React Native. Sur le web pur, les cookies httpOnly sont la seule source —
// lire un token stocké dans localStorage rouvrirait une surface XSS alors
// que la migration v10.5 vise justement à la fermer.
const isMobileWebView = () =>
  typeof window !== 'undefined' && !!window.ReactNativeWebView;

/**
 * Compatibilité app mobile React Native — le web n'en a plus besoin
 * Le web utilise les cookies httpOnly automatiquement
 */
export function getAuthHeaders() {
  if (!isMobileWebView()) return {};
  const token = localStorage.getItem('ekynair_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

/**
 * Tente de rafraîchir l'access token via le refresh token (cookie httpOnly)
 * Retourne true si succès, false sinon
 */
async function tryRefresh() {
  if (isRefreshing) {
    // Attendre que le refresh en cours se termine
    return new Promise(resolve => refreshQueue.push(resolve));
  }

  isRefreshing = true;
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    });

    if (res.ok) {
      const data = await res.json();
      // SEC-11 : mise à jour du token mobile uniquement dans le WebView RN.
      // Sur le web, le refresh renouvelle les cookies httpOnly côté serveur.
      if (data.token && isMobileWebView()) {
        localStorage.setItem('ekynair_token', data.token);
      }
      refreshQueue.forEach(resolve => resolve(true));
      refreshQueue = [];
      return true;
    }

    refreshQueue.forEach(resolve => resolve(false));
    refreshQueue = [];
    return false;
  } catch {
    refreshQueue.forEach(resolve => resolve(false));
    refreshQueue = [];
    return false;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Déconnexion : efface les cookies côté serveur + localStorage
 */
async function logout() {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
  } catch { /* best effort */ }
  // SEC-11 : nettoyage localStorage réservé au WebView mobile.
  if (isMobileWebView()) {
    localStorage.removeItem('ekynair_token');
  }
  window.location.href = '/';
}

/**
 * authFetch — Remplace fetch() pour tous les appels API authentifiés
 * Gère automatiquement le refresh silencieux sur 401
 */
export async function authFetch(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    credentials: 'include', // Envoie les cookies httpOnly automatiquement
    headers: {
      ...getAuthHeaders(), // Compatibilité mobile (Bearer token)
      ...options.headers,
    },
  });

  // Refresh silencieux si access token expiré
  if (res.status === 401) {
    let body = null;
    try { body = await res.clone().json(); } catch { /* ignore */ }

    // Retry silencieux pour TOKEN_EXPIRED ou NO_TOKEN :
    //  - TOKEN_EXPIRED : cookie présent mais JWT expiré (verify a rejeté)
    //  - NO_TOKEN      : cookie absent (browser a purgé après maxAge 15 min)
    // Dans les 2 cas, le refresh_token cookie (7j) peut émettre un nouveau
    // access_token. Pas de risque de boucle : le retry utilise fetch() et non
    // authFetch(), donc la récursion est impossible.
    if (body?.code === 'TOKEN_EXPIRED' || body?.code === 'NO_TOKEN') {
      // Retry refresh jusqu'à 2 fois avant de déclencher un logout
      for (let attempt = 1; attempt <= 2; attempt++) {
        const refreshed = await tryRefresh();
        if (refreshed) {
          // Rejouer la requête originale
          return fetch(url, {
            ...options,
            credentials: 'include',
            headers: {
              ...getAuthHeaders(),
              ...options.headers,
            },
          });
        }
        if (attempt < 2) await new Promise(r => setTimeout(r, 1000));
      }
    }

    // 2 refresh échoués, token invalide, ou autre code 401 → déconnexion
    await logout();
    return null;
  }

  return res;
}
